<script setup>
  import SpecGrid from '../Parts/SpecGrid.vue';
</script>

<template>
    <div class="content">
      <h1>Automatic Plex Torrenting</h1>

      <SpecGrid :specs="tech" title="Software Used" />

      <h2>Introduction</h2>
      <p>
        No more need to search through loads of <strong>dodgy websites</strong> for torrents! However, there's still a
        <strong>mild risk of bad torrents</strong>. I didn’t document everything step-by-step while setting it up,
        so this is a bit of a <strong>mind dump</strong> of how I did it.
      </p>

      <h2>Why I Chose a Virtual Machine Over Docker</h2>
      <p>
        I initially considered <strong>Docker</strong>, as all these applications have containerized versions. However,
        I <strong>trust torrents so little</strong> that I preferred using a <strong>dedicated VM</strong> and locking it
        down for security.
      </p>

      <h2>Software & Configuration</h2>
      <p>Here’s a breakdown of the <strong>essential software</strong> and how they fit together:</p>

      <h3>Radarr (Movies) - Default Port: 7878</h3>
      <p>
        Radarr is used to <strong>search for and download movies automatically</strong>. You’ll need to configure:
      </p>
      <ul>
        <li>Point it to the correct <strong>download directory</strong> (same as your torrent client).</li>
        <li>
          Set up <strong>a folder for completed torrents</strong>, so Radarr can automatically
          <strong>move files to the Plex library</strong>.
        </li>
      </ul>

      <h3>Sonarr (TV Shows) - Default Port: 8989</h3>
      <p>Essentially the same as <strong>Radarr</strong>, but for TV shows.</p>

      <h3>Jackett - Custom Torrent Indexers</h3>
      <p>
        Without <strong>Jackett</strong>, both <strong>Radarr</strong> and <strong>Sonarr</strong> would be
        <strong>pretty useless</strong>, so this is <strong>basically required</strong>.
        It allows you to connect to custom torrent sites by acting as a <strong>middleman</strong>.
      </p>
      <a href="https://github.com/Jackett/Jackett">Jackett Git hub</a>

      <p>
        My setup works great for movies, but TV shows are still <strong>a bit flaky</strong>—still ironing out the issues.
      </p>

      <img src="/assets/JacketExample.png" alt="Jackett Example" title="Jackett Example" />

      <h3>Configuring Jackett</h3>
      <p>
        - Grab your <strong>API Key</strong> from the <strong>top-right</strong> of Jackett’s home page.
        - Add your <strong>indexers</strong> to search engines like <strong>Sonarr & Radarr</strong>.
      </p>

      <img src="/assets/IndexersExample.png" alt="Indexers Example" title="Indexers Example" />

      <h3>Setting Up Your Download Client</h3>
      <p>You’ll need a torrent downloader like <strong>qBittorrent</strong>.</p>

      <img src="/assets/SonarrDownloader.png" alt="Sonarr Downloader Example" title="Sonarr Downloader Example" />

      <h3>Optimizing Torrenting Settings</h3>
      <p>
        To avoid <strong>seeding forever</strong>, I configured my torrent client to:
      </p>
      <ul>
        <li>Delete torrents <strong>30 minutes after seeding</strong>.</li>
        <li>Set up <strong>automatic file moving</strong> and check for new torrents every <strong>1 minute</strong>.</li>
      </ul>

      <h2>Security & VPN Configuration</h2>
      <p>
        I’m currently using <strong>NordVPN’s P2P servers</strong>, but I’m looking for a <strong>better and cheaper</strong>
        alternative. If you’re using NordVPN, make sure to:
      </p>
      <ul>
        <li>Set its <strong>priority very low</strong> to avoid interfering with all network traffic.</li>
        <li>Use <strong>split tunneling</strong>, so only the torrent client goes through the VPN.</li>
      </ul>

      <h2>Additional Software</h2>

      <h3>LazyLibrarian - Automatic Audiobook & Book Downloads</h3>
      <p>Uses <strong>Jackett</strong> to automate downloading books.</p>

      <h3>Audiobookshelf - Plex for Books</h3>
      <p>A great self-hosted audiobook and eBook server.</p>

      <h3>Web Server</h3>
      <p>Kept it simple and just <strong>hosted it inside the VM</strong>.</p>

      <h2>Making It Accessible Worldwide</h2>
      <p>To securely access everything remotely, I set up:</p>
      <ul>
        <li><strong>DuckDNS</strong> - Free dynamic DNS service.</li>
        <li><strong>Nginx</strong> - Used to <strong>route traffic</strong> to the correct services.</li>
        <li><strong>Host Header Matching</strong> - Ensures proper routing.</li>
        <li><strong>SSL & DNS via Cloudflare</strong> - Adds security.</li>
        <li><strong>FlexiCert</strong> - For handling SSL certificates.</li>
      </ul>
    </div>

</template>

<script>
  export default {
    data() {
      return {
        tech: [
          { component: "Plex Server", spec: "Used to watch videos" },
          { component: "Radarr", spec: "Used to automatically search movies" },
          { component: "Sonarr", spec: "Used to automatically search TV shows" },
          { component: "Jackett", spec: "Used to connect Radarr and Sonarr to torrent websites" },
          { component: "VPN", spec: "Used to reduce visibility of traffic on the server" },
          { component: "DuckDNS", spec: "Automatically updates DNS with your router's IP" },
          { component: "Nginx", spec: "Used to route traffic to different services" },
          { component: "Cloudflare", spec: "Used for DNS management and extra security" }
        ]
      };
    }
  };
</script>

